/**
 * Supabase Table Metadata Utility
 * Fetch real table information from Supabase for the SmartSecondarySidebar
 */

import { supabase } from '../lib/supabase';

export interface TableMetadata {
  table_name: string;
  table_schema: string;
  row_estimate: number;
  size_pretty: string;
  table_type: string;
  is_updatable: boolean;
  last_modified?: Date;
}

export interface TableWithRowCount {
  name: string;
  schema: string;
  rowCount: number;
  tableType: string;
  sizeEstimate: string;
  isUpdatable: boolean;
  lastModified?: Date;
  icon: string;
  category: 'core' | 'lookup' | 'system' | 'analysis';
}

// List of VRP system tables as specified in the requirements
const VRP_SYSTEM_TABLES = [
  'break_time_windows',
  'breaks', 
  'capacity_definitions',
  'code_examples',
  'crawled_pages',
  'datasets',
  'geography_columns',
  'geometry_columns',
  'job_skill_requirements', // Fixed: was 'job_skills'
  'jobs',
  'jobs_products',
  'location_clusters',
  'locations',
  'optimization_runs',
  'products',
  'project_users',
  'projects',
  'route_steps',
  'route_summaries',
  'scenarios',
  'skills_taxonomy', // Added: main skills table
  'shipments',
  'time_windows',
  'vehicles',
  'vehicle_skills', // Added: vehicle skills junction table
  'vehicle_types',
  'user_profiles' // Added: user profiles table
];

// Table categorization and icons
const TABLE_CONFIG = {
  // Core VRP entities
  'vehicles': { icon: '🚗', category: 'core' as const },
  'locations': { icon: '📍', category: 'core' as const },
  'jobs': { icon: '📦', category: 'core' as const },
  'products': { icon: '📦', category: 'core' as const },
  'skills': { icon: '🔧', category: 'core' as const },
  'projects': { icon: '📁', category: 'core' as const },
  'scenarios': { icon: '📋', category: 'core' as const },
  'datasets': { icon: '📊', category: 'core' as const },
  
  // Vehicle-related
  'vehicle_types': { icon: '🚛', category: 'core' as const },
  'capacity_definitions': { icon: '📏', category: 'core' as const },
  'breaks': { icon: '⏸️', category: 'core' as const },
  'break_time_windows': { icon: '⏰', category: 'core' as const },
  
  // Job-related
  'job_skill_requirements': { icon: '🔗', category: 'lookup' as const },
  'jobs_products': { icon: '🔗', category: 'lookup' as const },
  'shipments': { icon: '📦', category: 'core' as const },
  'time_windows': { icon: '⏰', category: 'core' as const },
  
  // Location-related
  'location_clusters': { icon: '🗺️', category: 'analysis' as const },
  'geography_columns': { icon: '🌍', category: 'system' as const },
  'geometry_columns': { icon: '📐', category: 'system' as const },
  
  // Optimization
  'optimization_runs': { icon: '⚡', category: 'analysis' as const },
  'route_steps': { icon: '🛣️', category: 'analysis' as const },
  'route_summaries': { icon: '📈', category: 'analysis' as const },
  
  // Knowledge management
  'code_examples': { icon: '💻', category: 'system' as const },
  'crawled_pages': { icon: '🕷️', category: 'system' as const },
  
  // User management
  'project_users': { icon: '👥', category: 'lookup' as const },
  'user_profiles': { icon: '👤', category: 'system' as const },
  
  // Skills system
  'skills_taxonomy': { icon: '🎯', category: 'core' as const },
  'vehicle_skills': { icon: '🔧', category: 'lookup' as const },
} as const;

/**
 * Fetch table metadata from Supabase information_schema
 */
export async function fetchTableMetadata(schema: string = 'public'): Promise<TableMetadata[]> {
  try {
    const { data, error } = await supabase.rpc('get_table_metadata', {
      target_schema: schema
    });

    if (error) {
      console.error('Error fetching table metadata:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch table metadata:', error);
    
    // Fallback: try direct query to information_schema
    return await fetchTableMetadataFallback(schema);
  }
}

/**
 * Fallback method using individual table queries
 */
async function fetchTableMetadataFallback(schema: string = 'public'): Promise<TableMetadata[]> {
  console.warn('[TableMetadata] Using fallback method - RPC function not available');
  
  // Return basic table structure using known VRP tables
  const tableMetadata: TableMetadata[] = [];
  
  for (const tableName of VRP_SYSTEM_TABLES) {
    try {
      // Try to get row count for this table
      const { count, error: countError } = await supabase
        .from(tableName)
        .select('*', { count: 'exact', head: true });

      if (countError) {
        // More specific error handling for different types of errors
        const errorMessage = countError.message || '';
        const errorCode = (countError as any)?.code || '';
        
        if (errorCode === 'PGRST116' || errorMessage.includes('does not exist')) {
          console.warn(`Table ${tableName} does not exist in database`);
        } else if (errorCode === '42501' || errorMessage.includes('permission denied')) {
          console.warn(`Table ${tableName} access denied - check RLS policies`);
        } else if (errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
          console.warn(`Table ${tableName} returned 400 error - likely RLS policy issue:`, errorMessage);
        } else {
          console.warn(`Table ${tableName} not accessible:`, errorMessage);
        }
        
        // Skip tables that don't exist or aren't accessible
        continue;
      }

      tableMetadata.push({
        table_name: tableName,
        table_schema: schema,
        row_estimate: count || 0,
        size_pretty: count ? `${count} rows` : '0 rows',
        table_type: 'BASE TABLE',
        is_updatable: true,
        last_modified: new Date()
      });
    } catch (error) {
      // Enhanced error handling for different error types
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      if (errorMessage.includes('relation') && errorMessage.includes('does not exist')) {
        console.warn(`Table ${tableName} does not exist in the database schema`);
      } else if (errorMessage.includes('permission denied') || errorMessage.includes('policy')) {
        console.warn(`Table ${tableName} access blocked by RLS policy - user may not have permission`);
      } else if (errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
        console.warn(`Table ${tableName} request failed with 400 error - likely authentication or RLS issue`);
      } else {
        console.warn(`Error processing table ${tableName}:`, errorMessage);
      }
      
      // Continue with next table - don't let one table failure stop the whole process
    }
  }

  return tableMetadata;
}

/**
 * Get actual row count for a specific table
 */
export async function getTableRowCount(tableName: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from(tableName)
      .select('id', { count: 'exact', head: true });

    if (error) {
      const errorMessage = error.message || '';
      const errorCode = (error as any)?.code || '';
      
      if (errorCode === 'PGRST116' || errorMessage.includes('does not exist')) {
        console.warn(`Table ${tableName} does not exist`);
      } else if (errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
        console.warn(`Table ${tableName} returned 400 error - likely RLS policy restricting access`);
      } else {
        console.warn(`Could not get row count for ${tableName}:`, errorMessage);
      }
      
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.warn(`Error getting row count for ${tableName}:`, error);
    return 0;
  }
}

/**
 * Transform raw table metadata into display-ready format
 */
export function transformTableMetadata(metadata: TableMetadata[]): TableWithRowCount[] {
  return metadata
    .filter(table => VRP_SYSTEM_TABLES.includes(table.table_name))
    .map(table => {
      const config = TABLE_CONFIG[table.table_name as keyof typeof TABLE_CONFIG] || {
        icon: '📊',
        category: 'system' as const
      };

      return {
        name: table.table_name,
        schema: table.table_schema,
        rowCount: table.row_estimate,
        tableType: table.table_type,
        sizeEstimate: table.size_pretty,
        isUpdatable: table.is_updatable,
        lastModified: table.last_modified,
        icon: config.icon,
        category: config.category
      };
    })
    .sort((a, b) => {
      // Sort by category first, then by name
      if (a.category !== b.category) {
        const categoryOrder = { core: 0, lookup: 1, analysis: 2, system: 3 };
        return categoryOrder[a.category] - categoryOrder[b.category];
      }
      return a.name.localeCompare(b.name);
    });
}

/**
 * Batch fetch row counts for multiple tables
 */
export async function batchFetchRowCounts(tableNames: string[]): Promise<Record<string, number>> {
  const results: Record<string, number> = {};
  
  // Process tables in batches to avoid overwhelming the database
  const batchSize = 5;
  for (let i = 0; i < tableNames.length; i += batchSize) {
    const batch = tableNames.slice(i, i + batchSize);
    
    const promises = batch.map(async (tableName) => {
      const count = await getTableRowCount(tableName);
      return { tableName, count };
    });
    
    const batchResults = await Promise.allSettled(promises);
    
    batchResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        results[result.value.tableName] = result.value.count;
      } else {
        console.warn(`Failed to get row count for ${batch[index]}:`, result.reason);
        results[batch[index]] = 0;
      }
    });
    
    // Small delay between batches to be respectful to the database
    if (i + batchSize < tableNames.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  return results;
}

/**
 * Hook-friendly function to get all VRP system tables with metadata
 */
export async function getVRPSystemTables(schema: string = 'public'): Promise<TableWithRowCount[]> {
  try {
    // First, get basic table metadata
    const metadata = await fetchTableMetadata(schema);
    
    // If no metadata returned, throw an error to trigger fallback
    if (!metadata || metadata.length === 0) {
      throw new Error('No table metadata available from RPC function');
    }
    
    // Transform to display format
    const tables = transformTableMetadata(metadata);
    
    // If we don't have row counts, fetch them
    const tablesNeedingCounts = tables.filter(t => t.rowCount === 0);
    if (tablesNeedingCounts.length > 0) {
      const rowCounts = await batchFetchRowCounts(tablesNeedingCounts.map(t => t.name));
      
      // Update row counts
      tables.forEach(table => {
        if (rowCounts[table.name] !== undefined) {
          table.rowCount = rowCounts[table.name];
        }
      });
    }
    
    return tables;
  } catch (error) {
    console.error('Error getting VRP system tables:', error);
    
    // Provide more helpful error information
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    
    if (errorMessage.includes('does not exist') || errorMessage.includes('get_table_metadata')) {
      console.error('Table metadata RPC function is not available. Using fallback method with available tables.');
      // Return the partial metadata from fallback instead of throwing
      return await fetchTableMetadataFallback(schema);
    } else if (errorMessage.includes('authentication') || errorMessage.includes('JWT')) {
      console.error('Authentication error - user may not be logged in or session expired');
      throw new Error('Authentication required to access table metadata');
    } else if (errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
      console.warn('Some tables returned 400 errors due to RLS policies - returning available tables');
      // Try the fallback method which handles individual table errors gracefully
      return await fetchTableMetadataFallback(schema);
    }
    
    // For other errors, try fallback method first before throwing
    try {
      console.warn('Primary method failed, attempting fallback method');
      return await fetchTableMetadataFallback(schema);
    } catch (fallbackError) {
      console.error('Both primary and fallback methods failed:', fallbackError);
      throw new Error(`Failed to load table metadata: ${errorMessage}`);
    }
  }
}